import { useAddress } from './use-address';
import Text from 'components/atomic/atoms/text';
import { typography } from 'design-system/tokens';
import { validateVariant } from 'ui/utils';

export const ADDRESS_VARIANTS = {
  HERO: 'hero',
  CARD: 'card',
  INLINE: 'inline',
} as const;

export type AddressVariant = typeof ADDRESS_VARIANTS[keyof typeof ADDRESS_VARIANTS];

const VARIANT_CONFIG = {
  [ADDRESS_VARIANTS.HERO]: {
    textVariant: 'body1' as const,
    component: 'h1' as const,
    fontSize: typography.responsive.headingMedium,
    lineHeight: { xs: typography.lineHeight.comfortable, md: typography.lineHeight.relaxed },
    color: 'text.secondary',
    truncate: false,
    itemProp: 'streetAddress',
  },
  [ADDRESS_VARIANTS.CARD]: {
    textVariant: 'h4' as const,
    component: 'h4' as const,
    fontSize: typography.fontSize.sm,
    lineHeight: undefined,
    color: 'text.secondary',
    truncate: true,
    itemProp: undefined,
  },
  [ADDRESS_VARIANTS.INLINE]: {
    textVariant: 'body2' as const,
    component: 'span' as const,
    fontSize: undefined,
    lineHeight: undefined,
    color: undefined,
    truncate: false,
    itemProp: undefined,
  },
};

export interface AddressProps {
  street: string | null;
  city?: string | null;
  province?: string | null;
  unitNumber?: string | null;
  variant: AddressVariant;
}

/**
 * Address UI component
 * 
 * Displays formatted address (street, city, province).
 * Variant is required - no default assumed.
 * 
 * Note: PropertyType is a separate UI component - compose them together
 * at the section level if needed.
 */
export function Address({
  street,
  city,
  province,
  unitNumber,
  variant,
}: AddressProps) {
  const { formatted } = useAddress({
    street: street || '',
    city: city || undefined,
    province: province || undefined,
    unitNumber,
  });

  if (!validateVariant('Address', variant, ADDRESS_VARIANTS)) {
    return null;
  }

  const config = VARIANT_CONFIG[variant];

  return (
    <Text
      variant={config.textVariant}
      component={config.component}
      sx={{
        fontSize: config.fontSize,
        lineHeight: config.lineHeight,
        color: config.color,
        ...(config.truncate && {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }),
      }}
      itemProp={config.itemProp}
      data-testid="ui-address"
    >
      {formatted}
    </Text>
  );
}
